import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStoredUser } from '../utils/authUser';
import {
  VISITOR_ANALYTICS_RANGES,
  getWebsiteVisitors
} from '../services/visitorAnalyticsService';
import './WebsiteVisitors.css';

const FILTERS = [
  { key: 'all', label: 'All visitors' },
  { key: 'high', label: 'High intent' },
  { key: 'returning', label: 'Returning' }
];

const VisitorIcon = ({ name }) => {
  const paths = {
    back: <path d="m15 18-6-6 6-6" />,
    people: <><circle cx="9" cy="8" r="3" /><path d="M3.5 19a5.5 5.5 0 0 1 11 0M16 6.5a2.5 2.5 0 0 1 0 5M16.5 14a4.5 4.5 0 0 1 4 4.5" /></>,
    return: <><path d="M4 9V4m0 5h5" /><path d="M5.8 7A8 8 0 1 1 4 14" /></>,
    spark: <><path d="m12 3 1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3Z" /><path d="m18 15 .8 2.2L21 18l-2.2.8L18 21l-.8-2.2L15 18l2.2-.8L18 15Z" /></>,
    home: <><path d="m3.5 11 8.5-7 8.5 7" /><path d="M6 9.5V20h12V9.5M9.5 20v-6h5v6" /></>,
    search: <><circle cx="10.5" cy="10.5" r="5.5" /><path d="m15 15 4.5 4.5" /></>,
    page: <><path d="M6 3.5h9l3 3V20H6z" /><path d="M14.5 3.5V7H18M9 11h6M9 15h6" /></>,
    clock: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7v5l3 2" /></>,
    shield: <><path d="M12 3.5 19 6v5.5c0 4.2-2.8 7.4-7 9-4.2-1.6-7-4.8-7-9V6l7-2.5Z" /><path d="m9.2 12 1.8 1.8 3.8-4" /></>,
    chevron: <path d="m9 7 5 5-5 5" />
  };
  return <svg viewBox="0 0 24 24" aria-hidden="true">{paths[name]}</svg>;
};

const formatRelativeTime = (value) => {
  if (!value) return 'No recent activity';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Activity time unavailable';
  const deltaMinutes = Math.round((date.getTime() - Date.now()) / 60000);
  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  if (Math.abs(deltaMinutes) < 60) return formatter.format(deltaMinutes, 'minute');
  const deltaHours = Math.round(deltaMinutes / 60);
  if (Math.abs(deltaHours) < 24) return formatter.format(deltaHours, 'hour');
  return formatter.format(Math.round(deltaHours / 24), 'day');
};

const formatEngagement = (seconds = 0) => {
  if (seconds < 60) return `${seconds}s engaged`;
  const minutes = Math.round(seconds / 60);
  return `${minutes} min engaged`;
};

const ActivityRow = ({ activity }) => {
  const icon = activity.type === 'listing_view'
    ? 'home'
    : activity.type === 'search' ? 'search' : 'page';
  let detail = <>Viewed {activity.pagePath || 'a page'}</>;
  if (activity.type === 'listing_view') {
    detail = activity.listingKey
      ? <>Viewed property <a href={`/listing/${encodeURIComponent(activity.listingKey)}`}>{activity.listingKey}</a></>
      : <>Viewed a property</>;
  } else if (activity.type === 'search') {
    detail = <>Searched{activity.searchTerm ? ` for “${activity.searchTerm}”` : ' listings'}</>;
  }

  return (
    <div className="visitor-activity-row">
      <span className={`visitor-activity-icon ${activity.type}`}><VisitorIcon name={icon} /></span>
      <span className="visitor-activity-detail">{detail}</span>
      <time dateTime={activity.occurredAt}>{formatRelativeTime(activity.occurredAt)}</time>
    </div>
  );
};

const WebsiteVisitors = () => {
  const navigate = useNavigate();
  const user = getStoredUser();
  const [range, setRange] = useState('30d');
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [expandedVisitor, setExpandedVisitor] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setStatus('loading');
    setError('');
    setExpandedVisitor(null);

    getWebsiteVisitors({ user, range, limit: 50, signal: controller.signal })
      .then(result => {
        setData(result);
        setStatus('ready');
      })
      .catch(requestError => {
        if (requestError.name === 'AbortError') return;
        setData(null);
        setError(requestError.message || 'Website visitor analytics are temporarily unavailable.');
        setStatus('error');
      });

    return () => controller.abort();
  }, [range, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const visitors = useMemo(() => {
    const available = data?.visitors || [];
    if (filter === 'high') return available.filter(visitor => visitor.intent?.level === 'high');
    if (filter === 'returning') return available.filter(visitor => visitor.returning);
    return available;
  }, [data, filter]);

  const summary = data?.summary || {};
  const listingAndSearchSignals = (summary.listingViews || 0) + (summary.searches || 0);

  return (
    <main className="website-visitors-page">
      <header className="website-visitors-header">
        <button type="button" className="website-visitors-back" onClick={() => navigate('/admin/client-manager')}>
          <VisitorIcon name="back" />
          Client Manager
        </button>
        <div className="website-visitors-heading">
          <span>Behavioral intelligence</span>
          <h1>Website Visitors</h1>
          <p>Understand anonymous demand without pretending to know who someone is.</p>
        </div>
        <div className="website-visitors-ranges" aria-label="Website visitor date range">
          {VISITOR_ANALYTICS_RANGES.map(option => (
            <button
              type="button"
              key={option}
              className={range === option ? 'active' : ''}
              onClick={() => setRange(option)}
            >
              {option}
            </button>
          ))}
        </div>
      </header>

      <section className="website-visitors-privacy-note">
        <span><VisitorIcon name="shield" /></span>
        <div>
          <strong>Privacy-safe by design</strong>
          <p>Each row represents an anonymous browser profile—not a known person. Labels are temporary, raw analytics identifiers stay on the server, and profiles tied to a sign-in during this date range are excluded.</p>
        </div>
      </section>

      {status === 'loading' && (
        <section className="website-visitors-loading" role="status">
          <span></span>
          <h2>Reading visitor signals</h2>
          <p>Organizing searches, listing views, sessions, and page activity.</p>
        </section>
      )}

      {status === 'error' && (
        <section className="website-visitors-state error" role="alert">
          <h2>We couldn’t load website visitors</h2>
          <p>{error}</p>
          <button type="button" onClick={() => setRefreshKey(key => key + 1)}>Try again</button>
        </section>
      )}

      {status === 'ready' && data?.configured === false && (
        <section className="website-visitors-state">
          <h2>Analytics is not configured</h2>
          <p>Connect this site’s GA4 property before anonymous visitor activity can appear.</p>
        </section>
      )}

      {status === 'ready' && data?.configured !== false && (
        <>
          <section className="website-visitors-summary" aria-label="Anonymous visitor summary">
            <article>
              <span className="website-visitors-summary-icon blue"><VisitorIcon name="people" /></span>
              <div><small>Profiles shown</small><strong>{summary.anonymousVisitors || 0}</strong><p>Anonymous browsers</p></div>
            </article>
            <article>
              <span className="website-visitors-summary-icon violet"><VisitorIcon name="return" /></span>
              <div><small>Returning</small><strong>{summary.returningVisitors || 0}</strong><p>Repeat browsing profiles</p></div>
            </article>
            <article>
              <span className="website-visitors-summary-icon rose"><VisitorIcon name="spark" /></span>
              <div><small>High intent</small><strong>{summary.highIntentVisitors || 0}</strong><p>Strong behavior signals</p></div>
            </article>
            <article>
              <span className="website-visitors-summary-icon green"><VisitorIcon name="home" /></span>
              <div><small>Intent actions</small><strong>{listingAndSearchSignals}</strong><p>{summary.listingViews || 0} listings · {summary.searches || 0} searches</p></div>
            </article>
          </section>

          <section className="website-visitors-list-shell">
            <div className="website-visitors-list-header">
              <div>
                <span>Anonymous audience</span>
                <h2>Visitor profiles</h2>
              </div>
              <div className="website-visitors-filters" aria-label="Filter website visitors">
                {FILTERS.map(option => (
                  <button
                    type="button"
                    key={option.key}
                    className={filter === option.key ? 'active' : ''}
                    onClick={() => setFilter(option.key)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {visitors.length === 0 ? (
              <div className="website-visitors-empty">
                <span><VisitorIcon name="people" /></span>
                <h3>No visitor profiles in this view</h3>
                <p>Try a longer date range or a different intent filter.</p>
              </div>
            ) : (
              <div className="website-visitors-list">
                {visitors.map((visitor, index) => {
                  const rowKey = `${visitor.label}-${visitor.lastSeenAt || index}`;
                  const expanded = expandedVisitor === rowKey;
                  return (
                    <article className={`website-visitor-card intent-${visitor.intent?.level || 'casual'} ${expanded ? 'expanded' : ''}`} key={rowKey}>
                      <button
                        type="button"
                        className="website-visitor-card-main"
                        onClick={() => setExpandedVisitor(expanded ? null : rowKey)}
                        aria-expanded={expanded}
                      >
                        <span className="website-visitor-avatar">{String(index + 1).padStart(2, '0')}</span>
                        <span className="website-visitor-identity">
                          <span className="website-visitor-title-row">
                            <strong>{visitor.label}</strong>
                            <span className={`website-visitor-intent ${visitor.intent?.level || 'casual'}`}>{visitor.intent?.label || 'Casual'}</span>
                            {visitor.returning && <span className="website-visitor-returning">Returning</span>}
                          </span>
                          <span className="website-visitor-signals">{visitor.intent?.signals?.join(' · ') || 'Browsing activity detected'}</span>
                        </span>
                        <span className="website-visitor-metrics">
                          <span><b>{visitor.visitCount || 1}</b> visits</span>
                          <span><b>{visitor.summary?.listingViews || 0}</b> listings</span>
                          <span><b>{visitor.summary?.searches || 0}</b> searches</span>
                        </span>
                        <span className="website-visitor-recency">
                          <VisitorIcon name="clock" />
                          <span><b>{formatRelativeTime(visitor.lastSeenAt)}</b><small>{formatEngagement(visitor.engagementSeconds)}</small></span>
                        </span>
                        <span className="website-visitor-chevron"><VisitorIcon name="chevron" /></span>
                      </button>

                      {expanded && (
                        <div className="website-visitor-activity">
                          <div className="website-visitor-activity-heading">
                            <div><span>Recent path</span><h3>What this browser explored</h3></div>
                            <p>Anonymous behavior only. There is no contact information available.</p>
                          </div>
                          {visitor.recentActivity?.length > 0 ? (
                            <div className="website-visitor-activity-list">
                              {visitor.recentActivity.map((activity, activityIndex) => (
                                <ActivityRow activity={activity} key={`${activity.occurredAt}-${activity.type}-${activityIndex}`} />
                              ))}
                            </div>
                          ) : (
                            <p className="website-visitor-no-activity">No detailed page, search, or listing events were available for this profile.</p>
                          )}
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}
    </main>
  );
};

export default WebsiteVisitors;
